import {useTranslation} from "react-i18next";
import React, {useContext, useEffect, useRef, useState} from "react";
import {FilterContext} from "../../../../../components/filters/DashboardFilterContextProvider.jsx";
import useAuthorityConfiguration from "../../../../../api/utilities/useAuthorityConfiguration.js";
import LoadingComponent from "../../../../../components/LoadingComponent.jsx";
import {ErpCalculationApi} from "../../../../../api/authority/generated/index";
import {useErrorTranslation} from "../../../../../api/utilities/translateErrorMessage.js";
import toast from "react-hot-toast";
import {Chart} from "chart.js";
import {useThemeProvider} from "../../../../../mosaic/utils/ThemeContext.jsx";
import {chartColors} from "../../../../../mosaic/charts/ChartjsConfig.jsx";
import moment from "moment-timezone";
import {getContrastBackgroundColors, getContrastBackgroundHoverColors} from "../../../../../utils/graphColors.js";
import Tooltip from "../../../../../components/Tooltip.jsx";
import {useRenderCurrencySign, useRenderPercentValue} from "../../../../../api/utilities/useValueRenderers.js";
import useUserSettings from "../../../../../utils/useUserSettings.js";

export default function PnoPanel({project, halfSize = false}) {

    let width = halfSize ? 'sm:col-span-6' : 'sm:col-span-4';

    let {t} = useTranslation();
    let {et} = useErrorTranslation();

    let {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);

    let authorityConfiguration = useAuthorityConfiguration();

    let renderPercentValue = useRenderPercentValue();
    let renderCurrencySign = useRenderCurrencySign();

    let[userSettings] = useUserSettings();

    let [totalPnoData, setTotalPnoData] = useState(null);
    let [totalPnoLoading, setTotalPnoLoading] = useState(true);
    useEffect(() => {
        let active = true;
        setTotalPnoLoading(true);

        new ErpCalculationApi(authorityConfiguration)
            .shopPerformancePNOTotal({
                projectId: project.id,
                erpFilter: {
                    since: since.format('YYYY-MM-DD'),
                    until: until.format('YYYY-MM-DD'),
                    selectedCentreIds: centresAllowListed ?? [],
                    selectedDomainIds: domainsAllowListed ?? [],
                }
            })
            .then((nextTotalPnoData) => {
                if (!active) {
                    return;
                }

                setTotalPnoData(nextTotalPnoData);
                setTotalPnoLoading(false);
            })
            .catch(e => {
                if (!active) {
                    return;
                }

                setTotalPnoLoading(false);
                et(e).then(toast.error);
            });

        return () => {
            active = false;
        };
    }, [project.id, since, until, centresAllowListed, domainsAllowListed]);

    /*let [totalWithoutOfflinePnoData, setTotalWithoutOfflinePnoData] = useState(null);
    useEffect(() => {
        new ErpCalculationApi(authorityConfiguration)
            .shopPerformancePNOTotalWithoutOffline({
                projectId: project.id,
                erpFilter: {
                    since: since.format('YYYY-MM-DD'),
                    until: until.format('YYYY-MM-DD'),
                    selectedCentreIds: centresAllowListed ?? [],
                    selectedDomainIds: domainsAllowListed ?? [],
                }
            })
            .then(setTotalWithoutOfflinePnoData)
            .catch(e => et(e).then(toast.error));
    }, [project.id, since, until, centresAllowListed, domainsAllowListed]);*/

    let [channelPnoData, setChannelPnoData] = useState(null);
    let [channelPnoLoading, setChannelPnoLoading] = useState(true);
    useEffect(() => {
        let active = true;
        setChannelPnoLoading(true);

        new ErpCalculationApi(authorityConfiguration)
            .shopPerformancePNOChannelsOnly({
                projectId: project.id,
                erpFilter: {
                    since: since.format('YYYY-MM-DD'),
                    until: until.format('YYYY-MM-DD'),
                    selectedCentreIds: centresAllowListed ?? [],
                    selectedDomainIds: domainsAllowListed ?? [],
                }
            })
            .then((nextChannelPnoData) => {
                if (!active) {
                    return;
                }

                setChannelPnoData(nextChannelPnoData);
                setChannelPnoLoading(false);
            })
            .catch(e => {
                if (!active) {
                    return;
                }

                setChannelPnoLoading(false);
                et(e).then(toast.error);
            });

        return () => {
            active = false;
        };
    }, [project.id, since, until, centresAllowListed, domainsAllowListed]);

    const totalPnoCanvas = useRef(null);
    /*const totalWithoutOfflinePnoCanvas = useRef(null);*/
    const channelsPnoCanvas = useRef(null);

    const {currentTheme} = useThemeProvider();
    const darkMode = currentTheme === 'dark';
    const {gridColor, textColor} = chartColors;

    const chartOptions = {
        maintainAspectRatio: false,
        animation: false,
        scales: {
            y: {
                border: {
                    display: false,
                },
                ticks: {
                    color: darkMode ? textColor.dark : textColor.light,
                },
                grid: {
                    color: darkMode ? gridColor.dark : gridColor.light,
                },
            },
            x: {
                border: {
                    display: false,
                },
                ticks: {
                    padding: 10,
                    align: 'center',
                    color: darkMode ? textColor.dark : textColor.light,
                },
                grid: {
                    display: false,
                },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: (chart) => {
                        return renderPercentValue(chart.raw);
                    }
                },
            },
        }
    };

    useEffect(() => {

        if (channelPnoData === null || userSettings === null) {
            return;
        }

        let chart = new Chart(
            channelsPnoCanvas.current,
            {
                type: 'line',
                data: {
                    labels: Object.keys(
                        channelPnoData?.pnoGranularByCurrency[0]?.pnoEntries ?? {}
                    ).map((key) => moment(key).format('DD.MM.')),

                    datasets: Object.values(channelPnoData?.pnoGranularByCurrency ?? []).map((value, index) => {
                        let values = [];

                        for (let pnoEntry of Object.values(value?.pnoEntries ?? [])) {
                            values.push(userSettings?.isMarketing ? pnoEntry.pnoGross : pnoEntry.pnoNet);
                        }

                        return {
                            label: t('dashboard.pno-of-currency', {currency: renderCurrencySign(value.currency)}),
                            backgroundColor: getContrastBackgroundColors()[4 + index],
                            hoverBackgroundColor: getContrastBackgroundHoverColors()[4 + index],
                            data: values,
                        };
                    }),
                },
                options: chartOptions
            });

        return () => {
            chart.destroy();
        };
    }, [project.id, channelPnoData, userSettings]);

    useEffect(() => {

        if (totalPnoData === null || userSettings === null) {
            return;
        }

        let chart = new Chart(
            totalPnoCanvas.current,
            {
                type: 'line',
                data: {
                    labels: Object.keys(
                        totalPnoData?.pnoGranularByCurrency[0]?.pnoEntries ?? {}
                    ).map((key) => moment(key).format('DD.MM.')),

                    datasets: Object.values(totalPnoData?.pnoGranularByCurrency ?? []).map((value, index) => {
                        let values = [];

                        for (let pnoEntry of Object.values(value?.pnoEntries ?? [])) {
                            values.push(userSettings?.isMarketing ? pnoEntry.pnoGross : pnoEntry.pnoNet);
                        }

                        return {
                            label: t('dashboard.pno-of-currency', {currency: renderCurrencySign(value.currency)}),
                            backgroundColor: getContrastBackgroundColors()[5 + index],
                            hoverBackgroundColor: getContrastBackgroundHoverColors()[5 + index],
                            data: values,
                        };
                    }),
                },
                options: chartOptions
            });

        return () => {
            chart.destroy();
        };
    }, [project.id, totalPnoData, userSettings]);

    /*useEffect(() => {
        if (totalWithoutOfflinePnoData === null || userSettings === null) {
            return;
        }

        let chart = new Chart(
            totalWithoutOfflinePnoCanvas.current,
            {
                type: 'line',
                data: {
                    labels: Object.keys(
                        totalWithoutOfflinePnoData?.pnoGranularByCurrency[0]?.pnoEntries ?? {}
                    ).map((key) => moment(key).format('DD.MM.')),

                    datasets: Object.values(totalWithoutOfflinePnoData?.pnoGranularByCurrency ?? []).map((value, index) => {
                        let values = [];

                        for (let pnoEntry of Object.values(value?.pnoEntries ?? [])) {
                            values.push(userSettings?.isMarketing ? pnoEntry.pnoGross : pnoEntry.pnoNet);
                        }

                        return {
                            label: t('dashboard.pno-of-currency', {currency: renderCurrencySign(value.currency)}),
                            backgroundColor: getContrastBackgroundColors()[5 + index],
                            hoverBackgroundColor: getContrastBackgroundHoverColors()[5 + index],
                            data: values,
                        };
                    }),
                },
                options: chartOptions
            });

        return () => {
            chart.destroy();
        };
    }, [project.id, totalWithoutOfflinePnoData, userSettings]);*/

    return [
        <LoadingComponent
            key={'pno-total'}
            loading={totalPnoLoading || totalPnoData === null || userSettings === null}
            className={
                "comp__roundedBox flex flex-col justify-between col-span-12 " + width +
                " bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700"
            }
        >
            <header
                className={'flex gap-2 px-4 sm:px-5 py-3 sm:py-4 border-b border-slate-100 dark:border-slate-700 items-center'}>
                <h3 className="text-xs md:text-sm font-semibold text-slate-800 dark:text-slate-100 uppercase">
                    {t('dashboard.total-shop-pno')}
                </h3>
                <Tooltip bg="dark" size="md" position="right" className={'inline-flex'}>
                    <div className="text-xs text-gray-200">
                        {t('tooltip.pno.total')}
                    </div>
                </Tooltip>
            </header>
            <div className="p-5 text-center flex items-center justify-center gap-10 grow-1 shrink-1">
                {Object.values(totalPnoData?.pnoTotalByCurrency ?? []).map((value) => {

                    let pnoValue = userSettings?.isMarketing ? value.pnoGross : value.pnoNet;

                    if (pnoValue <= 0) {
                        return null;
                    }

                    return <span
                        className="price text-l sm:text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100"
                        key={'pno' + value.currency}
                        title={value.currency}
                    >
                        {renderPercentValue(pnoValue)}
                    </span>;
                })}
            </div>
            <div className={'p-2 mt-auto'}>
                <div className="grow">
                    {<canvas ref={totalPnoCanvas}></canvas>}
                </div>
            </div>
        </LoadingComponent>,
        /*<LoadingComponent
            key={'pno-total-without-offline'}
            loading={totalWithoutOfflinePnoData === null || userSettings === null}
            className={
                "comp__roundedBox flex flex-col justify-between col-span-12 " + width +
                " bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700"
            }
        >
            <header
                className={'flex gap-2 px-4 sm:px-5 py-3 sm:py-4 border-b border-slate-100 dark:border-slate-700 items-center'}>
                <h3 className="text-xs md:text-sm font-semibold text-slate-800 dark:text-slate-100 uppercase">
                    {t('dashboard.total-shop-pno-without-offline')}
                </h3>
                <Tooltip bg="dark" size="md" position="right" className={'inline-flex'}>
                    <div className="text-xs text-gray-200">
                        {t('tooltip.pno.total-without-offline')}
                    </div>
                </Tooltip>
            </header>
            <div className="p-5 text-center flex items-center justify-center gap-10 grow-1 shrink-1">
                {Object.values(totalWithoutOfflinePnoData?.pnoTotalByCurrency ?? []).map((value) => {

                    let pnoValue = userSettings?.isMarketing ? value.pnoGross : value.pnoNet;

                    if (pnoValue <= 0) {
                        return null;
                    }

                    return <span
                        className="price text-l sm:text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100"
                        key={'pno' + value.currency}
                        title={value.currency}
                    >
                        {renderPercentValue(pnoValue)}
                    </span>;
                })}
            </div>
            <div className={'p-2 mt-auto'}>
                <div className="grow">
                    {<canvas ref={totalWithoutOfflinePnoCanvas}></canvas>}
                </div>
            </div>
        </LoadingComponent>,*/
        <LoadingComponent
            key={'pno-channels'}
            loading={channelPnoLoading || channelPnoData === null || userSettings === null}
            className={
                "comp__roundedBox flex flex-col justify-between col-span-12 " + width +
                " bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700"
            }
        >
            <header
                className={'flex gap-2 px-4 sm:px-5 py-3 sm:py-4 border-b border-slate-100 dark:border-slate-700 items-center'}>
                <h3 className="text-xs md:text-sm font-semibold text-slate-800 dark:text-slate-100 uppercase">
                    {t('dashboard.channels-only-pno')}
                </h3>

                <Tooltip bg="dark" size="md" position="left" className={'inline-flex'}>
                    <div className="text-xs text-gray-200">
                        {t('tooltip.pno.channels')}
                    </div>
                </Tooltip>
            </header>
            <div className="p-5 text-center flex items-center justify-center gap-10 grow-1 shrink-1">
                {Object.values(channelPnoData?.pnoTotalByCurrency ?? []).map((value) => {
                    let pnoValue = userSettings?.isMarketing ? value.pnoGross : value.pnoNet;

                    if (pnoValue <= 0) {
                        return null;
                    }

                    return <span
                        className="price text-l sm:text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100"
                        key={'pno' + value.currency}
                        title={value.currency}
                    >
                        {renderPercentValue(pnoValue)}
                    </span>;
                })}
            </div>
            <div className={'p-2 mt-auto'}>
                <div className="grow">
                    {<canvas ref={channelsPnoCanvas}></canvas>}
                </div>
            </div>
        </LoadingComponent>,
    ];
}
